import React, { useEffect, useMemo, useState } from 'react';

import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { chain, groupBy, uniqBy } from 'lodash';
import { DateTime } from 'luxon';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import type {
  PostReassignPositionsRequest,
  RosterAdultMember,
} from '@appTypes/esb';
import { OrgPositionDetail } from '@appTypes/esb/organizations/positions/_orgGuid';
import { getMYSTUrl } from '@config/config';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import {
  useLazyGetOrgPositionsQuery,
  usePostAssignFunctionalPositionsMutation,
  usePostExpireFunctionalPositionsMutation,
  usePostReassignPositionsMutation,
} from '@modules/rtk/esb-api';
import { Button, S, Spin } from '@modules/shared/components';
import { unitTypes } from '@shared/constants';
import {
  hasNewPermissionSel,
  organizationGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { toastService } from '@toasts';

import { InvalidPositions, Positions } from '../constants';
import { useOrgUnitRosterAdults, useUnitRosterAdults } from '../duck/hooks';
import { timeStampSel } from '../duck/selectors';
import { setPermsManagerTimeStamp } from '../duck/slice';
import {
  MockFunctionalPosition,
  MockRegisteredPosition,
  MultipleAssignFuncional,
  MultipleExpireFuncional,
  RosterAdultMemberWitRegistration,
} from '../types';
import styles from './assignees.less';
import { Counter } from './components/Counter';
import { PositionSelector } from './components/PositionSelector';
import { RegisteredWarningModal } from './components/RegisteredWarningModal';

const coolDownSeconds = 120;

export const Assignees = () => {
  const dispatch = useDispatch();
  const orgGuid = useSelector(organizationGuidSel) as string;
  const [assignFunctionalPosition, { isLoading: isSavingFunctionalPositon }] =
    usePostAssignFunctionalPositionsMutation();
  const [expireFunctionalPosition, { isLoading: isExpiringFunctionalPositon }] =
    usePostExpireFunctionalPositionsMutation();
  const [reAssignPositions, { isLoading: isReassigningPositon }] =
    usePostReassignPositionsMutation();
  const isSaving =
    isSavingFunctionalPositon ||
    isExpiringFunctionalPositon ||
    isReassigningPositon;
  const { isFetching: isLoadingUnitAdults, data: unitAdultsData } =
    useUnitRosterAdults();
  const {
    isFetching: isLoadingUnitRegistrationAdults,
    data: unitRegistrationAdultsData,
  } = useOrgUnitRosterAdults();
  const [
    getOrgPositions,
    { isLoading: isLoadingLazyOrgPositions, data: orgPositionsData },
  ] = useLazyGetOrgPositionsQuery();
  const [unitUsers, setUnitUsers] = useState<
    RosterAdultMemberWitRegistration[] | undefined
  >();
  const [functional, setFunctional] = useState<OrgPositionDetail[]>([]);
  const [registered, setRegistered] = useState<OrgPositionDetail[]>([]);
  const isLoading =
    isLoadingUnitAdults ||
    isLoadingLazyOrgPositions ||
    isLoadingUnitRegistrationAdults;
  const [isLocked, setIsLocked] = useState<boolean>(true);
  const timeStamp = useSelector(timeStampSel);
  const mystPositionManagerUrl = `${getMYSTUrl()}/${orgGuid}/organization-manager/position-manager`;
  const [shouldVerifyUsersWithoutRegPos, setShouldVerifyUsersWithoutRegPos] =
    useState<boolean>(false);
  const [tempRegistered, setTempRegistered] = useState<MockRegisteredPosition>(
    {},
  );

  const [tempFunctional, setTempFunctional] = useState<MockFunctionalPosition>(
    {},
  );
  const [errorsObj, setErrorsObj] = useState<
    Record<string, { hasError: boolean; message: string }>
  >({});
  const [usersWhithoutRegistered, setUserstWithoutRegistered] = useState<
    string[]
  >([]);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const hasLeadershipPerms = useSelector(state =>
    hasNewPermissionSel(
      state,
      newPermissions.EDIT_LEADER,
      newPermissions.EDIT_LEADER_RESTRICTED,
    ),
  );
  const hasPermsToSave = hasLeadershipPerms;

  useEffect(() => {
    if (!timeStamp) {
      getOrgPositions({ organizationGuid: orgGuid });
      setIsLocked(false);
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeStamp, dispatch, orgGuid]);

  const handleCountReachCero = () => {
    dispatch(setPermsManagerTimeStamp({ timeStamp: '' }));
  };

  useEffect(() => {
    const positions = orgPositionsData?.Positions;
    const isTroop = orgPositionsData?.orgType === unitTypes.TROOP;

    const invalidPositionsExtended = isTroop
      ? InvalidPositions
      : [...InvalidPositions, Positions.SUS];

    if (positions?.length) {
      const filteredPositions = positions.filter(
        pos =>
          !pos.isYouthPosition &&
          !(pos.min === 0 && pos.max === 0) &&
          !invalidPositionsExtended.includes(pos.positionLong),
      );
      const { true: functional = [], false: registered = [] } = chain(
        filteredPositions,
      )
        .orderBy(['positionLong'], ['asc'])
        .groupBy('isFunctional')
        .value();

      setRegistered(registered);
      setFunctional(functional);

      const tempRegisteredPositions: MockRegisteredPosition = {};
      registered.forEach(
        item =>
          (tempRegisteredPositions[`${item.PositionId}`] = {
            PositionId: `${item.PositionId}`,
            Assign: [],
            Expire: [],
          }),
      );
      setTempRegistered(tempRegisteredPositions);

      const tempFunctionalPositions: MockFunctionalPosition = {};
      functional.forEach(
        item =>
          (tempFunctionalPositions[`${item.PositionId}`] = {
            PositionId: `${item.PositionId}`,
            Assign: [],
            Expire: [],
          }),
      );
      setTempFunctional(tempFunctionalPositions);
      return;
    }

    setFunctional([]);
    setRegistered([]);
  }, [orgPositionsData]);

  const { key3Positions, programPositions } = useMemo(() => {
    if (registered.length) {
      const { true: key3Positions = [], false: programPositions = [] } =
        groupBy(registered, 'isKey3');
      return { key3Positions, programPositions };
    }

    return { key3Positions: [], programPositions: [] };
  }, [registered]);

  useEffect(() => {
    const users = unitAdultsData?.users;
    if (!users) {
      setUnitUsers(undefined);
      return;
    }
    if (!unitRegistrationAdultsData) {
      setUnitUsers(users as RosterAdultMemberWitRegistration[]);
      return;
    }

    //TODO: Change the use of roster (SB Legacy) for Akela or change the types for a custom one
    const reducedRegistData = featureFlags.getFlag(
      'SBL_6966_USE_ORGADULT_PERMMANAGER',
    )
      ? (uniqBy(
          unitRegistrationAdultsData.members,
          'personGuid',
        ) as unknown as RosterAdultMember[])
      : users;

    const rosterWithRegistration = reducedRegistData.map(user => {
      const userRegistrations = unitRegistrationAdultsData.members.filter(
        item => item.personGuid === user.personGuid,
      );

      if (userRegistrations.length) {
        const registrations = userRegistrations.map(item => ({
          personFullName: item.personFullName,
          position: item.position,
          positionId: item.positionId,
          registrationExpireDt: item.registrarInfo.registrationExpireDt,
          isFunctional: item.isFunctional,
          isExpired:
            DateTime.fromISO(item.registrarInfo.registrationExpireDt).startOf(
              'day',
            ) < DateTime.now().startOf('day'),
        }));

        return {
          ...user,
          registrations,
          mockRegistrations: registrations.filter(
            ({ isExpired }) => !isExpired,
          ),
        };
      }

      return {
        ...user,
        registrations: [],
      };
    }) as RosterAdultMemberWitRegistration[];
    setUnitUsers(rosterWithRegistration);
  }, [unitAdultsData, unitRegistrationAdultsData]);

  const positionsWithMoreUsersThanMax = useMemo(() => {
    if (!unitUsers?.length) return [];
    const allValidPositions = [...functional, ...registered];

    return allValidPositions.filter(pos => {
      if (pos.personsAssigned.length > pos.max) return false;

      const peopleWithValidRegistration = pos.personsAssigned.filter(person => {
        const foundPerson = unitUsers.find(
          item => item.personGuid === person.personGuid,
        );

        return !!foundPerson?.registrations?.find(
          reg => reg.positionId === pos.PositionId && !reg.isExpired,
        );
      });

      return peopleWithValidRegistration.length > pos.max;
    });
  }, [functional, registered, unitUsers]);

  useEffect(() => {
    if (!shouldVerifyUsersWithoutRegPos) return;
    setShouldVerifyUsersWithoutRegPos(false);

    const removedWithoutReassingation = Object.values(tempRegistered).reduce(
      (acc, item, _, tempRegisteredPositions) => {
        const usersWithNoRegistered = item.Expire.filter(
          guidToExpire =>
            !tempRegisteredPositions.find(registeresPos =>
              registeresPos.Assign.find(
                toAssign => toAssign.personguid === guidToExpire,
              ),
            ),
        ).map(item => item);
        return [...acc, ...usersWithNoRegistered];
      },
      [],
    );
    setUserstWithoutRegistered(removedWithoutReassingation);
  }, [tempRegistered, shouldVerifyUsersWithoutRegPos]);

  const handleAddRegistered = (PositionId: number, personGuid: string) => {
    const foundUserIndex =
      unitUsers?.findIndex(user => user.personGuid === personGuid) ?? -1;
    const foundUser =
      unitUsers && foundUserIndex !== -1
        ? unitUsers[foundUserIndex]
        : undefined;
    const alreadyHadPositionRegistration = foundUser?.registrations.find(
      reg => reg.positionId === PositionId && !reg.isExpired,
    );
    let expDtToSave =
      alreadyHadPositionRegistration?.registrationExpireDt ?? '';

    if (!expDtToSave) {
      const foundValidExpDt = foundUser?.registrations.find(
        reg => !reg.isExpired,
      )?.registrationExpireDt;
      expDtToSave = foundValidExpDt ?? DateTime.now().endOf('year').toISODate();
    }

    const positions = orgPositionsData?.Positions ?? [];
    const alreadyHadAssignation = positions
      .find(pos => pos.PositionId === PositionId)
      ?.personsAssigned?.find(person => person.personGuid === personGuid);

    //Add them to unit registrations
    if (foundUserIndex !== -1) {
      setUnitUsers(prevState => {
        if (prevState) {
          prevState[foundUserIndex].mockRegistrations.push({
            personFullName: prevState[foundUserIndex].personFullName,
            position: '',
            positionId: PositionId,
            registrationExpireDt: expDtToSave,
            isFunctional: false,
            isExpired: false,
          });
        }
        return prevState;
      });
    }

    if (foundUser) {
      //Add them to registered positions
      const updatedPersonAssigned = alreadyHadAssignation ?? {
        firstName: foundUser.firstName,
        lastName: foundUser.lastName,
        middleName: foundUser.middleName ?? '',
        fullName: foundUser.personFullName,
        personGuid: foundUser.personGuid,
        orgguid: '',
        orgType: '',
        gender: '',
        age: foundUser.age,
        registrationId: 0,
        pkpersonPositionId: 0,
        isCBCCompleted: true,
        effectiveDt: DateTime.now().toISODate(),
        expiryDt: expDtToSave,
        islapsed: false,
      };
      setRegistered(
        registered.map(reg => {
          if (reg.PositionId === PositionId) {
            const updatedReg = { ...reg };
            updatedReg.personsAssigned = [
              ...reg.personsAssigned,
              updatedPersonAssigned,
            ];
            return updatedReg;
          }

          return reg;
        }),
      );
    }

    const foundIndex = tempRegistered[`${PositionId}`].Expire.findIndex(
      item => item === personGuid,
    );
    if (!alreadyHadAssignation) {
      setTempRegistered(prevState => {
        const updateValue = {
          personguid: personGuid,
          EffectiveDt: DateTime.now().toISODate(),
          ExpiryDt: expDtToSave,
        };

        prevState[`${PositionId}`].Assign = [
          ...prevState[`${PositionId}`].Assign,
          updateValue,
        ];

        if (foundIndex !== -1) {
          const updateExpire = prevState[`${PositionId}`].Expire.filter(
            item => item !== personGuid,
          );
          prevState[`${PositionId}`].Expire = updateExpire;
        }

        return prevState;
      });
      setShouldVerifyUsersWithoutRegPos(true);
      return;
    }

    //Remove them from tempRegistered Expire if they are there
    if (foundIndex !== -1) {
      setTempRegistered(prevState => {
        const updateExpire = prevState[`${PositionId}`].Expire.filter(
          item => item !== personGuid,
        );
        prevState[`${PositionId}`].Expire = updateExpire;
        return prevState;
      });
      setShouldVerifyUsersWithoutRegPos(true);
    }
  };

  const handleRemoveRegistered = (PositionId: number, personGuid: string) => {
    const foundUserIndex =
      unitUsers?.findIndex(user => user.personGuid === personGuid) ?? -1;
    const positions = orgPositionsData?.Positions ?? [];
    const alreadyHadAssignation = positions
      .find(pos => pos.PositionId === PositionId)
      ?.personsAssigned?.find(person => person.personGuid === personGuid);

    //Remove them from unit registration
    if (foundUserIndex !== -1) {
      setUnitUsers(prevState => {
        if (prevState) {
          prevState[foundUserIndex].mockRegistrations = prevState[
            foundUserIndex
          ].mockRegistrations.filter(reg => reg.positionId !== PositionId);
        }
        return prevState;
      });
    }

    //Remove them from registered
    setRegistered(
      registered.map(reg => {
        if (reg.PositionId === PositionId) {
          const updatedReg = { ...reg };
          updatedReg.personsAssigned = reg.personsAssigned.filter(
            person => person.personGuid !== personGuid,
          );
          return updatedReg;
        }

        return reg;
      }),
    );

    //Add them to tempRegistered expire to save
    const foundIndex = tempRegistered[`${PositionId}`].Assign.findIndex(
      item => item.personguid === personGuid,
    );
    if (alreadyHadAssignation) {
      setTempRegistered(prevState => {
        if (!prevState) return prevState;

        prevState[`${PositionId}`].Expire = [
          ...prevState[`${PositionId}`].Expire,
          personGuid,
        ];

        if (foundIndex !== -1) {
          const updatedAssign = prevState[`${PositionId}`].Assign.filter(
            item => item.personguid !== personGuid,
          );
          prevState[`${PositionId}`].Assign = updatedAssign;
        }

        return prevState;
      });
      setShouldVerifyUsersWithoutRegPos(true);
      return;
    }

    //Remove them from tempRegistered Assign if they are there
    if (foundIndex !== -1) {
      setTempRegistered(prevState => {
        const updatedAssign = prevState[`${PositionId}`].Assign.filter(
          item => item.personguid !== personGuid,
        );
        prevState[`${PositionId}`].Assign = updatedAssign;
        return prevState;
      });
      setShouldVerifyUsersWithoutRegPos(true);
    }
  };

  const handleAddFunctional = (PositionId: number, personGuid: string) => {
    const foundUserIndex =
      unitUsers?.findIndex(user => user.personGuid === personGuid) ?? -1;
    const foundUser =
      unitUsers && foundUserIndex !== -1
        ? unitUsers[foundUserIndex]
        : undefined;
    const alreadyHadPositionRegistration = foundUser?.registrations.find(
      reg => reg.positionId === PositionId && !reg.isExpired,
    );
    let expDtToSave =
      alreadyHadPositionRegistration?.registrationExpireDt ?? '';

    if (!expDtToSave) {
      const foundValidExpDt = foundUser?.registrations.find(
        reg => !reg.isExpired,
      )?.registrationExpireDt;
      expDtToSave = foundValidExpDt ?? DateTime.now().endOf('year').toISODate();
    }

    const positions = orgPositionsData?.Positions ?? [];
    const alreadyHadAssignation = positions
      .find(pos => pos.PositionId === PositionId)
      ?.personsAssigned?.find(person => person.personGuid === personGuid);

    //Add them to unit registrations
    if (foundUserIndex !== -1) {
      setUnitUsers(prevState => {
        if (prevState) {
          prevState[foundUserIndex].mockRegistrations.push({
            personFullName: prevState[foundUserIndex].personFullName,
            position: '',
            positionId: PositionId,
            registrationExpireDt: expDtToSave,
            isFunctional: true,
            isExpired: false,
          });
        }
        return prevState;
      });
    }

    if (foundUser) {
      //Add them to functional positions
      const updatedPersonAssigned = alreadyHadAssignation ?? {
        firstName: foundUser.firstName,
        lastName: foundUser.lastName,
        middleName: foundUser.middleName ?? '',
        fullName: foundUser.personFullName,
        personGuid: foundUser.personGuid,
        orgguid: '',
        orgType: '',
        gender: '',
        age: foundUser.age,
        registrationId: 0,
        pkpersonPositionId: 0,
        isCBCCompleted: true,
        effectiveDt: DateTime.now().toISODate(),
        expiryDt: expDtToSave,
        islapsed: false,
      };
      setFunctional(
        functional.map(fun => {
          if (fun.PositionId === PositionId) {
            const updatedFun = { ...fun };
            updatedFun.personsAssigned = [
              ...fun.personsAssigned,
              updatedPersonAssigned,
            ];
            return updatedFun;
          }

          return fun;
        }),
      );
    }

    const foundIndex = tempFunctional[`${PositionId}`].Expire.findIndex(
      item => item.personGuid === personGuid,
    );
    if (!alreadyHadAssignation) {
      setTempFunctional(prevState => {
        const updateValue = {
          personGuid: personGuid,
          PositionId: `${PositionId}`,
          OrganizationGuid: orgGuid,
          EffectiveDt: DateTime.now().toISODate(),
          ExpiryDt: expDtToSave,
        };

        prevState[`${PositionId}`].Assign = [
          ...prevState[`${PositionId}`].Assign,
          updateValue,
        ];

        if (foundIndex !== -1) {
          const updateExpire = prevState[`${PositionId}`].Expire.filter(
            item => item.personGuid !== personGuid,
          );
          prevState[`${PositionId}`].Expire = updateExpire;
        }

        return prevState;
      });
      return;
    }

    //Remove them from tempRegistered Expire if they are there
    if (foundIndex !== -1) {
      setTempFunctional(prevState => {
        const updateExpire = prevState[`${PositionId}`].Expire.filter(
          item => item.personGuid !== personGuid,
        );
        prevState[`${PositionId}`].Expire = updateExpire;
        return prevState;
      });
    }
  };

  const handleRemoveFunctional = (
    PositionId: number,
    personGuid: string,
    PkPersonPositionId: number,
  ) => {
    const foundUserIndex =
      unitUsers?.findIndex(user => user.personGuid === personGuid) ?? -1;
    const positions = orgPositionsData?.Positions ?? [];
    const alreadyHadAssignation = positions
      .find(pos => pos.PositionId === PositionId)
      ?.personsAssigned?.find(person => person.personGuid === personGuid);

    //Remove them from unit registration
    if (foundUserIndex !== -1) {
      setUnitUsers(prevState => {
        if (prevState) {
          prevState[foundUserIndex].mockRegistrations = prevState[
            foundUserIndex
          ].mockRegistrations.filter(reg => reg.positionId !== PositionId);
        }
        return prevState;
      });
    }

    //Remove them from functional
    setFunctional(
      functional.map(reg => {
        if (reg.PositionId === PositionId) {
          const updatedReg = { ...reg };
          updatedReg.personsAssigned = reg.personsAssigned.filter(
            person => person.personGuid !== personGuid,
          );
          return updatedReg;
        }

        return reg;
      }),
    );

    //Add them to tempFunctional expire to save
    const foundIndex = tempFunctional[`${PositionId}`].Assign.findIndex(
      item => item.personGuid === personGuid,
    );
    if (alreadyHadAssignation) {
      setTempFunctional(prevState => {
        if (!prevState) return prevState;

        const updatedExpire = {
          personGuid: personGuid,
          PositionId: `${PositionId}`,
          PkPersonPositionId: PkPersonPositionId,
          OrganizationGuid: orgGuid,
          ExpiryDt: DateTime.now().toISODate(),
        };

        prevState[`${PositionId}`].Expire = [
          ...prevState[`${PositionId}`].Expire,
          updatedExpire,
        ];

        if (foundIndex !== -1) {
          const updatedAssign = prevState[`${PositionId}`].Assign.filter(
            item => item.personGuid !== personGuid,
          );
          prevState[`${PositionId}`].Assign = updatedAssign;
        }

        return prevState;
      });
      return;
    }

    //Remove them from tempFunctional Assign if they are there
    if (foundIndex !== -1) {
      setTempFunctional(prevState => {
        const updatedAssign = prevState[`${PositionId}`].Assign.filter(
          item => item.personGuid !== personGuid,
        );
        prevState[`${PositionId}`].Assign = updatedAssign;
        return prevState;
      });
    }
  };

  const handleSetError = (
    positionId: string,
    hasError: boolean,
    message: string,
  ) => {
    setErrorsObj(prevState => {
      prevState[positionId] = { hasError, message };
      return prevState;
    });
  };

  const handleSave = async () => {
    try {
      setShowWarningModal(false);
      const functionalExpirePayload: MultipleExpireFuncional[] = Object.values(
        tempFunctional,
      ).reduce((acc, item) => {
        const payloads = item.Expire.map(expireItem => ({
          personGuid: expireItem.personGuid,
          body: {
            PositionId: +expireItem.PositionId,
            PkPersonPositionId: expireItem.PkPersonPositionId,
            OrganizationGuid: expireItem.OrganizationGuid,
            ExpiryDt: expireItem.ExpiryDt,
          },
        }));

        return [...acc, ...payloads];
      }, []);

      const functionalAssignPayload: MultipleAssignFuncional[] = Object.values(
        tempFunctional,
      ).reduce((acc, item) => {
        const payloads = item.Assign.map(assignItem => ({
          personGuid: assignItem.personGuid,
          body: {
            PositionId: +assignItem.PositionId,
            OrganizationGuid: assignItem.OrganizationGuid,
            EffectiveDt: assignItem.EffectiveDt,
            ExpiryDt: assignItem.ExpiryDt,
          },
        }));

        return [...acc, ...payloads];
      }, []);

      const registeredPayload: PostReassignPositionsRequest = Object.values(
        tempRegistered,
      )
        .filter(pos => pos.Assign.length || pos.Expire.length)
        .map(item => ({
          PositionId: item.PositionId,
          Assign: item.Assign.map(assignItem => ({
            personguid: assignItem.personguid,
            EffectiveDt: assignItem.EffectiveDt,
            ExpiryDt: assignItem.ExpiryDt,
          })),
          Expire: item.Expire.map(expireItem => expireItem),
        }));

      if (functionalExpirePayload.length) {
        await Promise.all(
          functionalExpirePayload.map(payload =>
            expireFunctionalPosition({
              personGuid: payload.personGuid,
              body: payload.body,
              shouldInvalidateTags:
                !functionalAssignPayload.length && !registeredPayload.length,
            }).unwrap(),
          ),
        );
      }

      if (functionalAssignPayload.length) {
        await Promise.all(
          functionalAssignPayload.map(payload =>
            assignFunctionalPosition({
              personGuid: payload.personGuid,
              body: payload.body,
              shouldInvalidateTags: !registeredPayload.length,
            }).unwrap(),
          ),
        );
      }

      if (registeredPayload.length) {
        await reAssignPositions({
          orgGuid,
          body: registeredPayload,
          shouldInvalidateTags: false,
        }).unwrap();
      }
      toastService.success(
        <FormattedMessage id="permissionManager.assignees.successMessage" />,
      );

      //There is a desync between the reassign API and the get api, this is to try an mitigate that
      dispatch(setPermsManagerTimeStamp({ timeStamp: DateTime.now().toISO() }));
      setIsLocked(true);
      toastService.info(
        <FormattedMessage id="permissionManager.assignees.coolDownMessage" />,
      );
    } catch (error) {
      const err = error as FetchBaseQueryError;
      if (err.status === 403) return;
      toastService.error(error.data.errorDesc);
    }
  };

  const handleBeforeSave = () => {
    const errorList = Object.values(errorsObj);
    if (errorList.some(({ hasError }) => hasError)) {
      errorList
        .filter(item => item.hasError)
        .forEach(item => {
          toastService.error(item.message);
        });
      return;
    }

    setShowWarningModal(!!usersWhithoutRegistered.length);

    if (!usersWhithoutRegistered.length) {
      handleSave();
    }
  };

  return (
    <Spin delay={100} size="large" spinning={isLoadingLazyOrgPositions}>
      <RegisteredWarningModal
        visible={showWarningModal}
        onCancel={() => setShowWarningModal(false)}
        userGuidList={usersWhithoutRegistered}
        unitUsers={unitUsers}
      />
      <div>
        <S size="3" colored hr className={styles.sectionTitle}>
          <FormattedMessage id="permissionManager.assignees.sectionTitle.registered" />
        </S>
        {featureFlags.getFlag('SBL_6416_SHOW_REGISTERED_POSITIONS') ? (
          <>
            <S size="6" className={styles.lapsedNote}>
              <FormattedHTMLMessage id="permissionManager.assignees.lapsedNote" />
            </S>
            <S size="4" colored className={styles.sectionSubtitle}>
              <FormattedMessage id="permissionManager.assignees.sectionTitle.key3" />
            </S>
            <div className={styles.registeredContainer}>
              <div className={styles.sectionContainer}>
                {key3Positions.map(pos => (
                  <PositionSelector
                    key={pos.PositionId}
                    positionData={pos}
                    unitUsers={unitUsers}
                    loading={isLoading}
                    isSaving={isSaving}
                    disabled={isLocked || !hasPermsToSave}
                    positionsWithMoreUsersThanMax={
                      positionsWithMoreUsersThanMax
                    }
                    usersWhithoutRegistered={usersWhithoutRegistered}
                    registeredPositions={registered}
                    functionalPositions={functional}
                    onAddRegistered={handleAddRegistered}
                    onRemoveRegistered={handleRemoveRegistered}
                    onAddFunctional={handleAddFunctional}
                    onRemoveFunctional={handleRemoveFunctional}
                    onError={handleSetError}
                  />
                ))}
              </div>
              <S size="4" colored className={styles.sectionSubtitle}>
                <FormattedMessage id="permissionManager.assignees.sectionTitle.programAdminis" />
              </S>
              <div className={styles.sectionContainer}>
                {programPositions.map(pos => (
                  <PositionSelector
                    key={pos.PositionId}
                    positionData={pos}
                    unitUsers={unitUsers}
                    loading={isLoading}
                    isSaving={isSaving}
                    disabled={isLocked || !hasPermsToSave}
                    positionsWithMoreUsersThanMax={
                      positionsWithMoreUsersThanMax
                    }
                    usersWhithoutRegistered={usersWhithoutRegistered}
                    registeredPositions={registered}
                    functionalPositions={functional}
                    onAddRegistered={handleAddRegistered}
                    onRemoveRegistered={handleRemoveRegistered}
                    onAddFunctional={handleAddFunctional}
                    onRemoveFunctional={handleRemoveFunctional}
                    onError={handleSetError}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className={styles.mystContainer}>
            <Text>
              <FormattedMessage id="permissionManager.assignees.mystInstruction" />
              <a
                href={mystPositionManagerUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FormattedMessage id="settings.UnitPositions.managingPositions.myScouting" />
              </a>
            </Text>
          </div>
        )}

        <S size="3" colored hr className={styles.sectionTitle}>
          <FormattedMessage id="permissionManager.assignees.sectionTitle.functional" />
        </S>
        <S size="6" className={styles.lapsedNote}>
          <FormattedHTMLMessage id="permissionManager.assignees.lapsedNote" />
        </S>

        <div className={styles.sectionContainer}>
          {functional.map(pos => (
            <PositionSelector
              key={pos.PositionId}
              positionData={pos}
              unitUsers={unitUsers}
              loading={isLoading}
              isSaving={isSaving}
              disabled={isLocked || !hasPermsToSave}
              positionsWithMoreUsersThanMax={positionsWithMoreUsersThanMax}
              registeredPositions={registered}
              functionalPositions={functional}
              onAddRegistered={handleAddRegistered}
              onRemoveRegistered={handleRemoveRegistered}
              onAddFunctional={handleAddFunctional}
              onRemoveFunctional={handleRemoveFunctional}
              onError={handleSetError}
            />
          ))}
        </div>
        <div className={styles.stickyFooter}>
          {isLocked && (
            <Counter
              initialSeconds={
                timeStamp
                  ? Math.trunc(
                      DateTime.fromISO(timeStamp)
                        .plus({
                          seconds: coolDownSeconds,
                        })
                        .diff(DateTime.now())
                        .as('seconds'),
                    )
                  : 0
              }
              onReachZero={handleCountReachCero}
            />
          )}
          <Button
            type="primary"
            shape="round"
            size="large"
            onClick={handleBeforeSave}
            loading={isSaving || isLoading}
            disabled={isLocked || !hasPermsToSave}
          >
            Save
          </Button>
        </div>
      </div>
    </Spin>
  );
};
